import { Fragment, useId } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDown } from 'lucide-react';
import {
  BRANCHING_FINISH_VALUE,
  BRANCHING_TARGET_TYPE,
  getBranchingDestinationValue,
  parseBranchingDestinationValue,
} from './formularioGlobalBranching';

const groupTargets = (targets) =>
  targets.reduce((groups, target) => {
    const previousGroup = groups[groups.length - 1];

    if (target.tipo === BRANCHING_TARGET_TYPE.SECTION) {
      groups.push({
        key: target.key,
        sectionKey: target.key,
        title: target.titulo,
        number: target.numero,
        section: target,
        questions: [],
      });
      return groups;
    }

    if (
      target.secaoKey &&
      previousGroup &&
      previousGroup.sectionKey === target.secaoKey
    ) {
      previousGroup.questions.push(target);
      return groups;
    }

    groups.push({
      key: target.secaoKey || `root-${target.key}`,
      sectionKey: target.secaoKey,
      title: target.secao,
      number: target.secaoNumero,
      questions: [target],
    });
    return groups;
  }, []);

const FormularioGlobalQuestionBranching = ({
  disabled,
  onChange,
  option,
  randomizarPerguntas,
  showLabel,
  targets = [],
}) => {
  const intl = useIntl();
  const id = useId();
  const message = (messageId, values) =>
    intl.formatMessage({ id: messageId }, values);
  const destinationValue = getBranchingDestinationValue(option);
  const finish = destinationValue === BRANCHING_FINISH_VALUE;
  const target = targets.find((item) => item.value === destinationValue);
  const invalid = !!destinationValue && !finish && !target;
  const hasDestination = !!destinationValue && !randomizarPerguntas;
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const selectId = `${id}-destination`;
  const nextQuestion = targets.find(
    (item) => item.tipo === BRANCHING_TARGET_TYPE.QUESTION
  );
  const questionNumberMessage = (question, rootId, sectionId) =>
    question.secaoNumero
      ? message(sectionId, {
          secao: question.secaoNumero,
          numero: question.numero,
        })
      : message(rootId, { numero: question.numero });
  const defaultLabel = nextQuestion
    ? questionNumberMessage(
        nextQuestion,
        'labelProximaPerguntaRamificacao',
        'labelProximaPerguntaSecaoRamificacao'
      )
    : message('labelContinuarProximaPergunta');
  const sectionLabel = (section) =>
    message('labelIrParaInicioSecao', { numero: section.numero });
  const questionLabel = (question, includeSection = false) =>
    [
      questionNumberMessage(
        question,
        'labelIrParaPerguntaRamificacao',
        'labelIrParaPerguntaSecaoRamificacao'
      ),
      question.titulo,
      includeSection && question.secao ? `(${question.secao})` : '',
    ]
      .filter(Boolean)
      .join(' ');
  const targetLabel = (item) =>
    item.tipo === BRANCHING_TARGET_TYPE.SECTION
      ? [sectionLabel(item), item.titulo].filter(Boolean).join(' ')
      : questionLabel(item, true);
  const selectedLabel = finish
    ? message('labelConcluirPesquisa')
    : target
    ? targetLabel(target)
    : invalid
    ? message('labelPerguntaDestinoIndisponivel')
    : defaultLabel;

  const renderQuestionOption = (question) => (
    <option key={question.key} value={question.value}>
      {questionLabel(question)}
    </option>
  );

  return (
    <div className="tw:w-full tw:min-w-0 tw:@2xl:w-60 tw:@2xl:shrink-0">
      <label
        className={
          showLabel
            ? 'tw:mb-1 tw:block tw:h-4 tw:text-xs tw:font-semibold tw:text-slate-500'
            : 'tw:mb-1 tw:block tw:h-4 tw:text-xs tw:font-semibold tw:text-slate-500 tw:@2xl:sr-only'
        }
        htmlFor={selectId}
      >
        {message('labelAoEscolherIrPara')}
        <span className="tw:sr-only">: {option.texto || option.titulo}</span>
      </label>
      <div className="tw:relative">
        <select
          aria-describedby={`${descriptionId}${
            invalid && !randomizarPerguntas ? ` ${errorId}` : ''
          }`}
          aria-invalid={invalid && !randomizarPerguntas}
          className={`form-control tw:w-full tw:min-w-0 tw:appearance-none tw:truncate tw:rounded-md tw:border tw:pr-8 tw:pl-3 tw:outline-none tw:focus-visible:border-primary tw:focus-visible:ring-2 tw:focus-visible:ring-primary/20 tw:disabled:opacity-50 tw:aria-invalid:border-red-500 ${
            hasDestination
              ? 'tw:border-primary tw:bg-primary/10 tw:text-primary'
              : 'tw:border-dashed tw:border-slate-300 tw:bg-white tw:text-slate-500'
          }`}
          disabled={disabled || randomizarPerguntas}
          id={selectId}
          onChange={(event) =>
            onChange(parseBranchingDestinationValue(event.target.value))
          }
          title={selectedLabel}
          value={destinationValue}
        >
          <option value="">{defaultLabel}</option>
          <option value={BRANCHING_FINISH_VALUE}>
            {message('labelConcluirPesquisa')}
          </option>
          {invalid && (
            <option disabled value={destinationValue}>
              {message('labelPerguntaDestinoIndisponivel')}
            </option>
          )}
          {groupTargets(targets).map((group) => {
            if (!group.sectionKey) {
              return (
                <Fragment key={group.key}>
                  {group.questions.map(renderQuestionOption)}
                </Fragment>
              );
            }
            const sectionNumber = message('labelSecaoNumero', {
              numero: group.number,
            });
            return (
              <optgroup
                key={group.key}
                label={
                  group.title
                    ? `${group.title} — ${sectionNumber}`
                    : sectionNumber
                }
              >
                {group.section && (
                  <option value={group.section.value}>
                    {sectionLabel(group.section)}
                  </option>
                )}
                {group.questions.map(renderQuestionOption)}
              </optgroup>
            );
          })}
        </select>
        <ChevronDown
          aria-hidden="true"
          className={`tw:pointer-events-none tw:absolute tw:top-1/2 tw:right-3 tw:size-4 tw:-translate-y-1/2 ${
            hasDestination ? 'tw:text-primary' : 'tw:text-slate-500'
          }`}
        />
      </div>
      <p className="tw:sr-only" id={descriptionId}>
        {message(
          randomizarPerguntas
            ? 'msgRandomizacaoDesativaRamificacao'
            : 'msgRamificacaoOrientacao'
        )}
      </p>
      {!randomizarPerguntas && invalid && (
        <p
          className="tw:mb-0 tw:mt-1 tw:text-xs tw:text-red-600"
          id={errorId}
          role="alert"
        >
          {message('msgRamificacaoDestinoInvalido')}
        </p>
      )}
    </div>
  );
};

export default FormularioGlobalQuestionBranching;
